package org.moxultimate.topwithmox.wallpaper.api

import io.ktor.client.*
import io.ktor.client.request.*
import io.ktor.http.*
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flow
import org.moxultimate.topwithmox.core.base.BaseAPI.baseAPI
import org.moxultimate.topwithmox.core.handler.APIHandler
import org.moxultimate.topwithmox.core.state.APIState
import org.moxultimate.topwithmox.wallpaper.data.*

/**
 * WallpaperRepository
 * Handles all read operations for wallpapers: fetching lists, single items, series, and tags.
 * Write operations (upload, update, delete, saveSeries, saveTag) live in WallpaperCRUDRepository.
 *
 * @param httpClient Ktor HTTP client used for GET requests.
 */
class WallpaperRepository(
    private val httpClient: HttpClient,
) {
    private val baseUrl = "$baseAPI/wallpapers"
    private val wallpaperSeriesUrl = "$baseAPI/wallpaper-series"
    private val wallpaperTagUrl = "$baseAPI/wallpaper-tag"

    /**
     * getWallpaperById
     * Fetches a single wallpaper by its [id] via GET /wallpapers/{id}.
     *
     * @param id ID of the wallpaper to fetch.
     */
    fun getWallpaperById(id: Long): Flow<APIState<WallpaperResponse>> = flow {
        emit(APIState.Loading)
        emit(APIHandler.safeApiCall { httpClient.get("$baseUrl/$id") })
    }

    /**
     * getAllWallpapers
     * Fetches up to 20 wallpapers whose id is less than [beforeId] (cursor-based pagination).
     * Pass Long.MAX_VALUE (the default) to load the first page.
     *
     * @param beforeId Cursor — id of the last item from the previous page.
     */
    fun getAllWallpapers(beforeId: Long = Long.MAX_VALUE): Flow<APIState<List<WallpaperResponse>>> = flow {
        emit(APIState.Loading)
        emit(APIHandler.safeApiCall {
            httpClient.get(baseUrl) {
                parameter("beforeId", beforeId)
            }
        })
    }

    /**
     * getWallpapersBySeries
     * Fetches up to 20 wallpapers belonging to [request.series] starting after [request.lastId].
     * Pass lastId = 0 in the request to load the first page.
     *
     * @param request Encapsulates the series name and pagination cursor.
     */
    fun getWallpapersBySeries(request: WallpaperSeriesRequest): Flow<APIState<List<WallpaperResponse>>> = flow {
        emit(APIState.Loading)
        emit(APIHandler.safeApiCall {
            httpClient.get("$baseUrl/series/${request.series}") {
                parameter("lastId", request.lastId)
            }
        })
    }

    /**
     * getFilteredWallpapers
     * Fetches up to 20 wallpapers matching the combined chip filters via GET /wallpapers/filter.
     * - [types]: OR'd list of type keywords (e.g. ["phone", "vertical"]) — empty = no type filter.
     * - [series]: OR'd list of series names (e.g. ["black cars", "frieren"]) — empty = no series filter.
     * - When both are non-empty they are AND'd: wallpaper must match at least one type AND one series.
     *
     * @param types  Type keywords to match in tags (OR between them).
     * @param series Series names to match in tags (OR between them).
     * @param has360 When true, only wallpapers that have a 360° panorama.
     * @param beforeId Pagination cursor — id of the last item from the previous page.
     */
    fun getFilteredWallpapers(
        types: List<String>,
        series: List<String>,
        has360: Boolean = false,
        beforeId: Long = Long.MAX_VALUE,
    ): Flow<APIState<List<WallpaperResponse>>> = flow {
        emit(APIState.Loading)
        emit(APIHandler.safeApiCall {
            httpClient.get("$baseUrl/filter") {
                if (types.isNotEmpty()) parameter("types", types.joinToString(","))
                if (series.isNotEmpty()) parameter("series", series.joinToString(","))
                if (has360) parameter("has360", true)
                parameter("beforeId", beforeId)
            }
        })
    }

    /**
     * searchWallpapers
     * Searches wallpapers by free text via POST /wallpapers/search. Every word of the query must
     * partially match the tags or title; chip filters in the request narrow the result further.
     *
     * @param request Query text, active chips and the pagination cursor.
     */
    fun searchWallpapers(request: WallpaperSearchRequest): Flow<APIState<List<WallpaperResponse>>> = flow {
        emit(APIState.Loading)
        emit(APIHandler.safeApiCall {
            httpClient.post("$baseUrl/search") {
                contentType(ContentType.Application.Json)
                setBody(request)
            }
        })
    }

    /**
     * getWallpapersByType
     * Fetches up to 20 wallpapers of the given [type] (case-insensitive) whose id is less than [beforeId].
     * Pass Long.MAX_VALUE (the default) to load the first page — newest first.
     *
     * @param type Wallpaper type: Phone, Desktop, Widescreen, or Vertical.
     * @param beforeId Cursor — id of the last item from the previous page.
     */
    fun getWallpapersByType(type: String, beforeId: Long = Long.MAX_VALUE): Flow<APIState<List<WallpaperResponse>>> = flow {
        emit(APIState.Loading)
        emit(APIHandler.safeApiCall {
            httpClient.get("$baseUrl/type/$type") {
                parameter("beforeId", beforeId)
            }
        })
    }

    /**
     * getWallpapersByTag
     * Fetches up to 20 wallpapers whose tags contain [tag] (case-insensitive) starting after [lastId].
     * Pass lastId = 0 to load the first page.
     *
     * @param tag The tag string to search for.
     * @param lastId Cursor for pagination — id of the last item from the previous page.
     */
    fun getWallpapersByTag(tag: String, lastId: Long = 0L): Flow<APIState<List<WallpaperResponse>>> = flow {
        emit(APIState.Loading)
        emit(APIHandler.safeApiCall {
            httpClient.get("$baseUrl/tag/$tag") {
                parameter("lastId", lastId)
            }
        })
    }

    /**
     * getAllSeries
     * Fetches all series from GET /wallpaper-series and maps each entity to its series name.
     * Used to populate series chips regardless of wallpaper pagination state.
     */
    fun getAllSeries(): Flow<APIState<List<String>>> = flow {
        emit(APIState.Loading)
        emit(
            APIHandler.safeApiCall<List<WallpaperSeriesEntity>, List<String>>(
                apiToBeCalled = { httpClient.get(wallpaperSeriesUrl) },
                transformer = { entities -> entities.map { it.series }.distinct() },
            )
        )
    }

    /**
     * getAllTags
     * Fetches all tags from GET /wallpaper-tag and maps each entity to its tag string.
     * Used to populate autocomplete suggestions in the search bar.
     */
    fun getAllTags(): Flow<APIState<List<String>>> = flow {
        emit(APIState.Loading)
        emit(
            APIHandler.safeApiCall<List<WallpaperTagsEntity>, List<String>>(
                apiToBeCalled = { httpClient.get(wallpaperTagUrl) },
                transformer = { entities -> entities.map { it.tag }.distinct() },
            )
        )
    }
}
